import { compressToBase64, decompressFromBase64 } from 'lz-string';
import React from 'react';

/**
 * Compresse et convertit une image en Base64 en utilisant des techniques avancées d'optimisation:
 * - Conversion systématique en WebP
 * - Redimensionnement de toutes les images selon les dimensions maximales
 * - Suppression des métadonnées
 * - Compression optimisée
 * 
 * @param {File} file - Fichier image à traiter
 * @param {number} maxSizeKB - Taille maximale en Ko (par défaut: 150 Ko)
 * @param {number} maxWidth - Largeur maximale en pixels (par défaut: 1024px)
 * @param {number} maxHeight - Hauteur maximale en pixels (par défaut: 800px)
 * @returns {Promise<string>} - Image encodée en Base64 et compressée
 */
export const imageToBase64 = async (file, maxSizeKB = 150, maxWidth = 1024, maxHeight = 800) => {
  const MAX_SIZE_BYTES = maxSizeKB * 1024; // Conversion en bytes
  
  return new Promise((resolve, reject) => {
    // Créer un objet FileReader pour lire le contenu du fichier
    const reader = new FileReader();
    
    reader.onload = () => {
      // Charger l'image pour le traitement
      const img = new Image();
      img.onload = () => {
        // Étape 1: Redimensionner l'image selon les dimensions maximales
        let width = img.width;
        let height = img.height;
        
        // Calculer les nouvelles dimensions tout en conservant le ratio d'aspect
        if (width > maxWidth || height > maxHeight) {
          const ratioWidth = maxWidth / width;
          const ratioHeight = maxHeight / height;
          const ratio = Math.min(ratioWidth, ratioHeight); // Prendre le plus petit ratio pour respecter les deux contraintes
          
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
        }
        
        // Fonction pour vérifier si WebP est supporté par le navigateur
        const isWebPSupported = () => {
          const canvas = document.createElement('canvas');
          if (canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0) {
            return true;
          }
          return false;
        };

        // Utiliser WebP si supporté, sinon JPEG
        const imageFormat = isWebPSupported() ? 'image/webp' : 'image/jpeg';
        
        // Fonction pour générer l'image avec les dimensions et la qualité spécifiées
        const generateOptimizedImage = (w, h, q, format) => {
          // Créer un canvas à la taille cible
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          
          // Appliquer un anti-aliasing pour une meilleure qualité
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // Dessiner l'image sur le canvas
          ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, w, h);
          
          // Retourner l'URL de données et estimer sa taille
          const dataUrl = canvas.toDataURL(format, q);
          const size = new Blob([dataUrl]).size;
          return { dataUrl, size };
        };
        
        // Qualité par défaut pour les petites images
        let quality = isWebPSupported() ? 0.85 : 0.92; // WebP a une meilleure compression, on peut utiliser une qualité plus basse
        
        // Même pour les petites images, nous allons les optimiser
        let result = generateOptimizedImage(width, height, quality, imageFormat);
        
        // Si l'image est plus grande que la taille maximale, réduire progressivement la qualité
        if (result.size > MAX_SIZE_BYTES) {
          const qualityLevels = [0.80, 0.75, 0.70, 0.65, 0.60, 0.55, 0.50, 0.45, 0.40];
          
          for (const q of qualityLevels) {
            result = generateOptimizedImage(width, height, q, imageFormat);
            if (result.size <= MAX_SIZE_BYTES) {
              quality = q;
              break;
            }
          }
          
          // Si la réduction de qualité ne suffit pas, réduire les dimensions
          if (result.size > MAX_SIZE_BYTES) {
            const scaleFactors = [0.8, 0.7, 0.6, 0.5, 0.4];
            
            for (const scale of scaleFactors) {
              const newWidth = Math.floor(width * scale);
              const newHeight = Math.floor(height * scale);
              
              for (const q of [0.7, 0.6, 0.5, 0.4]) {
                result = generateOptimizedImage(newWidth, newHeight, q, imageFormat);
                if (result.size <= MAX_SIZE_BYTES) {
                  width = newWidth;
                  height = newHeight;
                  quality = q;
                  break;
                }
              }
              
              if (result.size <= MAX_SIZE_BYTES) break;
            }
          }
        }
        
        // En dernier recours, si l'image est toujours trop grande
        if (result.size > MAX_SIZE_BYTES) {
          const finalWidth = Math.floor(width * 0.4);
          const finalHeight = Math.floor(height * 0.4);
          result = generateOptimizedImage(finalWidth, finalHeight, 0.4, imageFormat);
        }
        
        // Log de l'optimisation
        const formatName = isWebPSupported() ? 'WebP' : 'JPEG';
        console.log(`Image optimisée: ${Math.round(result.size / 1024)}/${maxSizeKB}KB, dimensions: ${width}x${height}, format: ${formatName}, qualité: ${Math.round(quality * 100)}%`);
        
        // Compresser avec lz-string et résoudre la promesse
        const compressed = compressToBase64(result.dataUrl);
        resolve(compressed);
      };
      
      img.onerror = (error) => {
        reject(new Error("Erreur lors du chargement de l'image pour optimisation"));
      };
      
      // Définir la source de l'image avec le contenu du fichier
      img.src = reader.result;
    };
    
    reader.onerror = (error) => {
      reject(new Error("Erreur lors de la lecture du fichier"));
    };
    
    // Lire le fichier comme une URL de données
    reader.readAsDataURL(file);
  });
};

/**
 * Décompresse une image Base64
 * @param {string} base64String - Chaîne Base64 compressée
 * @returns {string} - URL de données de l'image
 */
export const base64ToImage = (base64String) => {
  try {
    // Vérifier si la chaîne est déjà une URL de données (data:image/...)
    if (base64String.startsWith('data:image/')) {
      return base64String;
    }

    // Décompresser la chaîne et vérifier si le résultat est une URL de données
    const decompressed = decompressFromBase64(base64String);
    if (decompressed && decompressed.startsWith('data:image/')) {
      return decompressed;
    }

    // Si la décompression ne donne pas une image valide, retourner la chaîne originale
    return base64String;
  } catch (error) {
    console.error('Erreur lors de la décompression de l\'image:', error);
    // En cas d'erreur, retournons la chaîne originale (peut-être qu'elle n'est pas compressée)
    return base64String;
  }
};

/**
 * Vérifie si un contenu est une image Base64
 * @param {string} content - Contenu à vérifier
 * @returns {boolean} - true si c'est une image Base64, false sinon
 */
export const isBase64Image = (content) => {
  if (!content || typeof content !== 'string') return false;
  
  try {
    // Essayons de décompresser - si c'est une image compressée, ça devrait fonctionner
    const decompressed = decompressFromBase64(content);
    // Vérifier si le résultat ressemble à une image en base64
    return decompressed && decompressed.startsWith('data:image/');
  } catch (error) {
    // Vérifier si c'est une image non compressée
    return content.startsWith('data:image/');
  }
};

/**
 * Formate le contenu d'un verbatim en tableau mixte texte/image
 * @param {any} content - Contenu à formater
 * @returns {Array} - Tableau formaté
 */
export const formatVerbatimContent = (content) => {
  // Si le contenu est un tableau, c'est un format mixte texte/image
  if (Array.isArray(content)) {
    return content;
  }
  
  // Si c'est une chaîne de caractères, c'est probablement du texte simple
  if (typeof content === 'string') {
    return [{ type: 'text', content: content }];
  }
  
  // Fallback au cas où
  return [{ type: 'text', content: String(content) }];
};

/**
 * Convertit les anciens verbatims au nouveau format
 * @param {Object} verbatim - Verbatim à convertir
 * @returns {Object} - Verbatim converti
 */
/**
 * Normalizes a verbatim link value so it always becomes a string.
 * @param {any} link - Raw value read from JSON or Excel.
 * @returns {string} - Normalized link value.
 */
export const normalizeVerbatimLink = (link) => {
  if (typeof link === 'string') {
    return link;
  }

  if (typeof link === 'number' || typeof link === 'boolean') {
    return String(link);
  }

  if (link && typeof link === 'object') {
    if (typeof link.hyperlink === 'string' && link.hyperlink.trim() !== '') {
      return link.hyperlink;
    }

    if (typeof link.text === 'string' && link.text.trim() !== '') {
      return link.text;
    }

    if (Array.isArray(link.richText)) {
      const richTextValue = link.richText.find(
        (part) => typeof part?.text === 'string' && part.text.trim() !== ''
      )?.text;

      if (richTextValue) {
        return richTextValue;
      }
    }
  }

  return '';
};

const isRenderableVerbatimBlock = (block) => (
  !!block
  && typeof block === 'object'
  && typeof block.type === 'string'
  && ['text', 'image'].includes(block.type.trim().toLowerCase())
  && typeof block.content === 'string'
  && block.content.trim() !== ''
);

/**
 * Returns the current defect state for a verbatim based on its stored status.
 * @param {Object} verbatim - Verbatim to inspect.
 * @returns {{ isDefective: boolean, message: string }}
 */
export const getVerbatimDefectInfo = (verbatim) => {
  const issues = [];
  const linkValue = normalizeVerbatimLink(verbatim?.link).trim();
  const content = verbatim?.verbatim;

  if (!linkValue) {
    issues.push('Link is empty.');
  }

  if (typeof content === 'string') {
    if (!content.trim()) {
      issues.push('Verbatim content is empty.');
    }
  } else if (Array.isArray(content)) {
    if (!content.some(isRenderableVerbatimBlock)) {
      issues.push('Verbatim content is empty.');
    }
  } else {
    issues.push('Verbatim content is empty.');
  }

  const message = [...new Set(issues)].join(' ');

  return {
    isDefective: issues.length > 0,
    message,
  };
};

/**
 * Re-applies the defect status on a verbatim after a manual edit.
 * @param {Object} verbatim - Verbatim to normalize.
 * @returns {Object} - Verbatim with a refreshed defect status.
 */
export const normalizeVerbatimDefectStatus = (verbatim) => {
  if (!verbatim || typeof verbatim !== 'object') {
    return verbatim;
  }

  const nextVerbatim = { ...verbatim };
  const defectInfo = getVerbatimDefectInfo(nextVerbatim);

  if (defectInfo.isDefective) {
    nextVerbatim.status = 'error';
    nextVerbatim.statusMessage = defectInfo.message;
  } else {
    delete nextVerbatim.status;
    delete nextVerbatim.statusMessage;
  }

  return nextVerbatim;
};

export const migrateVerbatimFormat = (verbatim) => {
  if (!verbatim) return verbatim;
  
  // Si le verbatim n'a pas de propriété "verbatim" ou si elle est déjà un tableau, pas besoin de migration
  if (!verbatim.verbatim || Array.isArray(verbatim.verbatim)) {
    return verbatim;
  }
  
  // Vérifier si c'est une image Base64
  if (isBase64Image(verbatim.verbatim)) {
    return {
      ...verbatim,
      verbatim: [{ type: 'image', content: verbatim.verbatim }]
    };
  }
  
  // Sinon c'est du texte
  return {
    ...verbatim,
    verbatim: [{ type: 'text', content: verbatim.verbatim }]
  };
};

/**
 * Extracts every valid text block from a verbatim.
 * @param {Object} verbatim - Verbatim containing a string or mixed content blocks.
 * @returns {string} - Plain text ready to be included in a prompt.
 */
export const getVerbatimText = (verbatim) => {
	const content = verbatim?.verbatim;

	if (typeof content === 'string') {
		return content.trim();
	}

	if (!Array.isArray(content)) {
		return '';
	}

	return content
		.filter((item) => item?.type === 'text' && typeof item.content === 'string')
		.map((item) => item.content.trim())
		.filter(Boolean)
		.join('\n');
};

/**
 * Indicates whether a verbatim has a usable translation.
 * @param {Object} verbatim - Verbatim to inspect.
 * @returns {boolean} - True when the translation contains non-whitespace text.
 */
export const hasVerbatimTranslation = (verbatim) => (
	typeof verbatim?.translation === 'string'
	&& verbatim.translation.trim() !== ''
);

export const renderVerbatimTranslation = (translation) => {
  if (typeof translation !== 'string' || translation.trim() === '') {
    return null;
  }

  return <div className="verbatim-translation-content">{translation}</div>;
};
